import { DOCUMENT, inject, Injectable } from '@angular/core';

import { Dictionary } from '../interfaces/dictionary';
import { WINDOW } from '../services/window.service';

/**
 * Browser URL and location helpers scoped to the Angular hash-routing
 * conventions used by this portal. Exists because native `URLSearchParams`
 * mishandles Angular hash URLs (`/#/path?key=value`) — the query string
 * can sit before OR after the hash fragment depending on how navigation
 * arrived, and `URLSearchParams` does not peel off the fragment itself.
 *
 * Also provides a single place to mock `window.location` and
 * `window.history` in tests via the `WINDOW` injection token.
 */
@Injectable({
  providedIn: 'root'
})
export class URLUtils {
  private readonly window = inject(WINDOW);
  document = inject<Document>(DOCUMENT);

  public replaceHistoryState(url: string): void {
    this.window.history.replaceState({}, this.document.title, url);
  }
  public get userAgent(): string {
    return this.window.navigator.userAgent;
  }
  public replaceLocation(url): void {
    this.document.location.replace(url);
  }

  /**
   * Parses query string parameters from a URL (or `document.location.search`
   * if no URL provided). Handles the Angular hash-routing edge case by
   * stripping the fragment before parsing, and supports `list[]=` repeated-key
   * syntax by collecting values into an array. Parameter keys are lowercased
   * because backend auth flows (CAS ticket parameter) use inconsistent casing.
   */
  public getAllUrlParams(url = ''): { [key: string]: string } {
    let query = url ? url.split('?')[1] : this.document.location.search.slice(1);
    const params: Dictionary = {};

    if (query) {
      [query] = query.split('#');
      const arr = query.split('&');
      arr.forEach(param => {
        const p = param.split('=');
        // in case params look like: list[]=thing1&list[]=thing2
        let paramNum;
        let paramName = p[0].replace(/\[\d*\d]/, v => {
          paramNum = v.slice(1, -1);
          return '';
        });

        const paramValue = typeof p[1] === 'undefined' ? true : p[1];
        paramName = paramName.toLowerCase();
        this.parseParam(params, paramName, paramNum, paramValue);
      });
    }

    return params;
  }

  private parseParam(params: Dictionary, paramName: string, paramNum, paramValue: boolean | string) {
    if (params[paramName]) {
      // convert value to array (if still string)
      if (typeof params[paramName] === 'string') {
        params[paramName] = [params[paramName]];
      }
      // if no array index number specified...
      if (typeof paramNum === 'undefined') {
        // put the value on the end of the array
        params[paramName].push(paramValue);
      } else {
        // put the value at that index number
        params[paramName][paramNum] = paramValue;
      }
    } else {
      params[paramName] = paramValue;
    }
  }

  /**
   * Cleans url from ticket used for authentication
   * (redirect from CAS)
   *
   * @param pathParams
   */
  public clearUrl(pathParams: { [key: string]: string }, excludedKey: string): string {
    let query = this.document.location.origin + this.document.location.pathname + this.document.location.hash;

    const pathParamsKeys = Object.keys(pathParams).filter(key => key !== excludedKey);
    if (pathParamsKeys.length) {
      const search = pathParamsKeys.map(key => `${key}=${pathParams[key]}`).join('&');
      query += `?${search}`;
    }
    return query;
  }
  /**
   * Detects legacy IE/Edge-HTML browsers. Matches IE (`msie`, `trident/`)
   * and pre-Chromium Edge (`edge/`). Used to show a "browser not supported"
   * error page — the app targets modern evergreen browsers only (see NFR-2.3).
   */
  public get isInternetExplorer(): boolean {
    return /msie\s|trident\/|edge\//i.test(this.userAgent);
  }

  /** Encoded current page URL for round-tripping through an external SSO login. */
  public generateRedirectBackUrl(): string {
    return encodeURIComponent(this.document.location.href);
  }

  /**
   * Redirects the browser to the CAS login page, appending the current
   * page URL as a `return` parameter so CAS can bounce the user back
   * after successful authentication.
   */
  public redirectToCASLoginPage(casURL: string): void {
    const redirectBackTo = this.generateRedirectBackUrl();
    const redirectTo = casURL + redirectBackTo;
    this.replaceLocation(redirectTo);
  }
}
